"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.retireReasonSchema = exports.updateReasonSchema = exports.createReasonSchema = exports.REJECTION_STAGE_LABELS = exports.NO_RECENT_USE = exports.REASON_RECENT_JOBS = exports.REASON_RECENT_DAYS = exports.SORTING_EXCLUDED_REASON_CODES = exports.LOCKED_REASON_CODES = exports.REASON_LIST_LABELS = exports.REASON_LISTS = exports.PREVIOUS_OUTCOME_LABELS = exports.PREVIOUS_OUTCOMES = exports.restoreApplicationSchema = exports.endSortingSchema = exports.sortingHeartbeatSchema = exports.undoDecisionSchema = exports.saveSortingNoteSchema = exports.sortingDecisionSchema = exports.changeSortSchema = exports.nextCardsSchema = exports.startSortingSchema = exports.AUTO_REJECT_MIN_APPLICATIONS = exports.AUTO_REJECT_WARNING_PERCENT = exports.UNSCREENED_DIGEST_THRESHOLD = exports.JOB_AGING_DAYS = exports.REGRET_TEMPLATE_KEYS = exports.REGRET_TIMING_HINTS = exports.REGRET_TIMING_LABELS = exports.DEFAULT_REGRET_TIMING = exports.REGRET_TIMINGS = exports.MAX_UNSENT_DECISIONS = exports.BREAK_PROMPT_EVERY = exports.NOTE_COUNTER_FROM = exports.NOTE_MAX_LENGTH = exports.UNDO_DEPTH = exports.SORTING_HEARTBEAT_SECONDS = exports.SORTING_WINDOW = exports.SORTING_PREFETCH = exports.SORTING_LEASE_MINUTES = exports.SORT_ORDER_LABELS = exports.DEFAULT_SORT_ORDER = exports.SORT_ORDERS = exports.SORTING_QUEUE_STAGES = void 0;
exports.isRegretSent = isRegretSent;
exports.regretTemplateKey = regretTemplateKey;
exports.customReasonPrefix = customReasonPrefix;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// S15 — sorting mode (SCR-01), the auto-rejected list (SCR-04) and the reason lists
// (ADMIN-07). Sources: master plan S15 and its planning decisions (DECISIONS-LOG S15
// Q1–Q7); the SCR-01 spec in 05-screen-inventory Part 5; F8.01–F8.12; §L6, §L14,
// §L19, §L20; TECH-STACK 2.6 for the lease.
//
// R6 (§L31, ADR 0041): sorting mode is HR's review card. A shortlists (S15's advance,
// which no longer moves anyone on), H is Maybe (S15's hold, now a recorded status that
// comes back once To review is empty), R rejects with a reason, Ctrl+Z undoes.
// --- The queue ----------------------------------------------------------------------
/** E6 step 2: the queue is everything in `new` or `screening` still To review or Maybe (§L31). */
exports.SORTING_QUEUE_STAGES = ['new', 'screening'];
/** F8.09. */
exports.SORT_ORDERS = ['score_desc', 'applied_asc', 'applied_desc'];
exports.DEFAULT_SORT_ORDER = 'score_desc';
exports.SORT_ORDER_LABELS = {
    // §L31: final score, then import order.
    score_desc: 'Best fit first',
    applied_asc: 'Oldest first',
    applied_desc: 'Newest first',
};
/** SCR-01 §13: a card is held while shown and freed after five idle minutes. */
exports.SORTING_LEASE_MINUTES = 5;
/** F8.02: the next three load while the current one is read. */
exports.SORTING_PREFETCH = 3;
/** The card on screen and the three behind it — the lease covers all four (S15 planning). */
exports.SORTING_WINDOW = 1 + exports.SORTING_PREFETCH;
/** How often an open, in-use sorting screen renews its leases. Well inside the five minutes. */
exports.SORTING_HEARTBEAT_SECONDS = 60;
/** F8.07. */
exports.UNDO_DEPTH = 5;
/** SCR-01 §9. */
exports.NOTE_MAX_LENGTH = 5000;
exports.NOTE_COUNTER_FROM = 4500;
/** O9, answered in S15 Q6: a gentle, non-blocking prompt every hundred decisions. */
exports.BREAK_PROMPT_EVERY = 100;
/** More decisions than this waiting to send is a connection long gone, not a busy morning. */
exports.MAX_UNSENT_DECISIONS = 500;
// --- Regret emails (S15 Q1, closes O8) ---------------------------------------------------
/**
 * Workflow review C7 (ADR 0076): `none` ("Don't send") is the default for new jobs. It stops only the
 * email — the rejection, its reason and its event are recorded exactly as before.
 */
exports.REGRET_TIMINGS = ['none', 'on_close', 'next_working_day'];
exports.DEFAULT_REGRET_TIMING = 'none';
exports.REGRET_TIMING_LABELS = {
    none: 'Don’t send',
    on_close: 'When the job closes',
    next_working_day: 'The next working day',
};
exports.REGRET_TIMING_HINTS = {
    none: 'No email. The rejection and its reason are still recorded.',
    on_close: 'Sent to everyone not selected when the job closes.',
    next_working_day: 'Sent the morning after, so a mistake can be undone first.',
};
/** Whether a job's rejections queue a letter on their own; the closure screen's tick is asked separately. */
function isRegretSent(timing) {
    return timing !== 'none';
}
/** §L19 "Candidate message tone", as the email each tone sends. */
exports.REGRET_TEMPLATE_KEYS = {
    STANDARD: 'application.regret.standard',
    WARM: 'application.regret.warm',
    WARM_OTHER_ROLES: 'application.regret.warm_other_roles',
    REAPPLY: 'application.regret.reapply',
};
/**
 * §L19's tone column. A pool-eligible reason — or pooling the person — gets the
 * warmer letter; SCR-INCOMPLETE asks them to apply again; SCR-IRRELEVANT points at
 * other openings. A reason the HR Lead adds follows its pool switch.
 */
function regretTemplateKey(reasonCode, triggersPool, isPooled) {
    if (reasonCode === 'SCR-INCOMPLETE')
        return exports.REGRET_TEMPLATE_KEYS.REAPPLY;
    if (reasonCode === 'SCR-IRRELEVANT')
        return exports.REGRET_TEMPLATE_KEYS.WARM_OTHER_ROLES;
    return triggersPool || isPooled ? exports.REGRET_TEMPLATE_KEYS.WARM : exports.REGRET_TEMPLATE_KEYS.STANDARD;
}
// --- The alerts S15 sends (S15 Q7) ------------------------------------------------------
/** N-11: "+14 days" with nobody past screening. */
exports.JOB_AGING_DAYS = 14;
/** N-15: "daily digest ≥25". */
exports.UNSCREENED_DIGEST_THRESHOLD = 25;
/** N-18 and the S15 build notes: "above 60% means badly configured knockouts". */
exports.AUTO_REJECT_WARNING_PERCENT = 60;
/**
 * N-18 needs enough applicants for a rate to mean anything — one auto-rejection out
 * of one is 100%. Not a documented number; recorded in ADR 0017.
 */
exports.AUTO_REJECT_MIN_APPLICATIONS = 10;
// --- Requests -------------------------------------------------------------------------
const noteSchema = zod_1.z.string().max(exports.NOTE_MAX_LENGTH, `Notes can be up to ${exports.NOTE_MAX_LENGTH.toLocaleString('en-GB')} characters.`);
const poolNameSchema = zod_1.z.string().trim().min(1, 'Give the pool a name.').max(80, 'Pool names can be up to 80 characters.');
/** A card read for longer than a day is a tab left open, not a dwell time. */
const dwellSchema = zod_1.z.number().int().nonnegative().max(86_400_000).optional();
exports.startSortingSchema = zod_1.z.object({
    sortOrder: zod_1.z.enum(exports.SORT_ORDERS).optional(),
    /** S16 "Sort selected": only these, from SCR-02. Absent is the whole queue. */
    onlyApplicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1).max(200).optional(),
});
exports.nextCardsSchema = zod_1.z.object({
    count: zod_1.z.number().int().min(1).max(exports.SORTING_WINDOW),
    /** The cards on screen: kept, renewed, and reported if they are no longer this screen's. */
    holding: zod_1.z.array(schemas_1.uuidSchema).max(exports.SORTING_WINDOW).default([]),
    /**
     * Also never hand out: cards decided here but not yet sent — which after a long
     * offline spell can be many (state h), so the cap is generous. Not reported on: their
     * decision may land first, and that is not someone else deciding them.
     */
    exclude: zod_1.z.array(schemas_1.uuidSchema).max(exports.MAX_UNSENT_DECISIONS).default([]),
});
exports.changeSortSchema = zod_1.z.object({
    sortOrder: zod_1.z.enum(exports.SORT_ORDERS),
    /** The card on screen stays; everything prefetched behind it is given back. */
    keep: zod_1.z.array(schemas_1.uuidSchema).max(1).default([]),
});
const decisionBase = zod_1.z.object({
    /** Made in the browser, so a decision replayed after going offline is recorded once. */
    clientDecisionId: schemas_1.uuidSchema,
    applicationId: schemas_1.uuidSchema,
    note: noteSchema.optional(),
    noteActivityId: schemas_1.uuidSchema.nullable().optional(),
    dwellMs: dwellSchema,
});
const rejectFields = {
    reasonCode: zod_1.z.string().trim().min(1, 'Pick a reason before rejecting.').max(40),
    poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
    newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
};
exports.sortingDecisionSchema = zod_1.z
    .discriminatedUnion('action', [
    decisionBase.extend({ action: zod_1.z.literal('shortlist') }),
    decisionBase.extend({ action: zod_1.z.literal('maybe') }),
    // DECISIONS-LOG 2026-10-19 Group 3 Q2: the same "Also add to CV Bank" tick as the board's reject box.
    decisionBase.extend({ action: zod_1.z.literal('reject'), ...rejectFields, addToCvBank: zod_1.z.boolean().default(false) }),
    decisionBase.extend({ action: zod_1.z.literal('pool'), ...rejectFields }),
])
    .superRefine((input, ctx) => {
    if (input.action !== 'pool')
        return;
    if (input.poolIds.length + input.newPoolNames.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['poolIds'], message: 'Choose at least one pool.' });
    }
});
exports.saveSortingNoteSchema = zod_1.z.object({
    applicationId: schemas_1.uuidSchema,
    note: noteSchema,
    activityId: schemas_1.uuidSchema.nullable().optional(),
});
exports.undoDecisionSchema = zod_1.z.object({ decisionId: schemas_1.uuidSchema });
exports.sortingHeartbeatSchema = zod_1.z.object({
    /** The cards on this screen right now: their leases are renewed. */
    holding: zod_1.z.array(schemas_1.uuidSchema).max(20).default([]),
});
exports.endSortingSchema = zod_1.z.object({
    /** The card on screen when they left, for its dwell time. */
    applicationId: schemas_1.uuidSchema.optional(),
    dwellMs: dwellSchema,
});
exports.restoreApplicationSchema = schemas_1.versionedSchema;
// --- Views ------------------------------------------------------------------------------
/** §L14: what a reapplicant's earlier try may show here — never its scores. */
exports.PREVIOUS_OUTCOMES = ['in_progress', 'not_selected', 'withdrew', 'hired', 'job_closed', 'moved'];
exports.PREVIOUS_OUTCOME_LABELS = {
    in_progress: 'Still in progress',
    not_selected: 'Not selected',
    withdrew: 'Withdrew',
    hired: 'Hired',
    job_closed: 'Job closed',
    moved: 'Moved to another role',
};
// --- ADMIN-07 (S15 Q5) -----------------------------------------------------------------------
exports.REASON_LISTS = ['rejection', 'withdrawal'];
exports.REASON_LIST_LABELS = {
    rejection: 'Rejection reasons',
    withdrawal: 'Withdrawal reasons',
};
/**
 * S15 Q5: reasons the system itself relies on. GEN-DUPLICATE closes merges,
 * GEN-DNR and INT-INTEGRITY set do-not-rehire, SCR-KNOCKOUT is what knockouts record.
 * WD-PROCESS and WD-EXPERIENCE are §L20's two dashboard signals, locked for the same
 * reason (ADR 0017). SCR-HM-NO is what "Reject the No's" preselects (§L31, R6); SCR-PHONE is
 * what Out on a phone screening records (ADR 0079).
 */
exports.LOCKED_REASON_CODES = [
    'GEN-DUPLICATE',
    'SCR-HM-NO',
    'SCR-PHONE',
    'GEN-DNR',
    'INT-INTEGRITY',
    'SCR-KNOCKOUT',
    'WD-PROCESS',
    'WD-EXPERIENCE',
];
/**
 * Offered nowhere in sorting mode: a duplicate is closed by a merge (S14 Q9),
 * do-not-rehire is set on the candidate with its restricted reason (F7.13), and an
 * offer withdrawn by the company is recorded by the withdrawal itself (S23 Q6).
 */
exports.SORTING_EXCLUDED_REASON_CODES = ['GEN-DUPLICATE', 'GEN-DNR', 'GEN-OFFER-WITHDRAWN'];
/** ADMIN-07's "Last 30 days" window (Admin redesign 2026-11-02). */
exports.REASON_RECENT_DAYS = 30;
/** How many jobs the "Last 30 days" line names; the rest are summed. */
exports.REASON_RECENT_JOBS = 3;
exports.NO_RECENT_USE = { total: 0, jobs: [], otherJobs: 0 };
exports.REJECTION_STAGE_LABELS = {
    screening: 'Screening',
    assessment: 'Assessment',
    interview: 'Interview',
    any: 'Any stage',
};
const reasonLabelSchema = zod_1.z
    .string()
    .trim()
    .min(3, 'Give the reason a label of at least 3 characters.')
    .max(80, 'Labels can be up to 80 characters.');
exports.createReasonSchema = zod_1.z
    .object({
    list: zod_1.z.enum(exports.REASON_LISTS),
    stage: zod_1.z.enum(['screening', 'assessment', 'interview', 'any']).nullable().default(null),
    label: reasonLabelSchema,
    triggersPool: zod_1.z.boolean().default(false),
})
    .superRefine((input, ctx) => {
    if (input.list === 'rejection' && input.stage === null) {
        ctx.addIssue({ code: 'custom', path: ['stage'], message: 'Choose the stage this reason belongs to.' });
    }
});
exports.updateReasonSchema = schemas_1.versionedSchema.extend({
    label: reasonLabelSchema.optional(),
    triggersPool: zod_1.z.boolean().optional(),
});
exports.retireReasonSchema = schemas_1.versionedSchema;
/** The prefix a new reason's code takes, so a report still groups it with its stage. */
function customReasonPrefix(list, stage) {
    if (list === 'withdrawal')
        return 'WD';
    const prefixes = { screening: 'SCR', assessment: 'ASM', interview: 'INT', any: 'GEN' };
    return prefixes[stage ?? 'any'];
}
//# sourceMappingURL=sorting.js.map